// Client-side payment service. It knows nothing about Stripe: it asks the
// server for a hosted checkout URL and later asks the server for the
// authoritative purchase status. The browser redirect never grants access.

import { useQuery } from '@tanstack/react-query';
import { invokeFunction } from '@/lib/functions';
import { openExternal } from '@/lib/platform/browser';
import { rpc } from '@/lib/rpc';
import { supabase } from '@/lib/supabase/client';
import type { Purchase } from '@/types/domain';

export interface PayoutSettings {
  auto_withdraw: boolean;
  threshold_cents: number;
  keep_cents: number;
  min_withdrawal_cents: number;
}

export interface PayoutResult {
  payout_id: string;
  amount_cents: number;
  currency: string;
  status: 'pending' | 'in_transit' | 'paid' | 'failed' | 'cancelled';
  arrival_date: string | null;
}

/** Money waiting in the provider's own payout account (held by the payment partner, not TradePrep). */
export interface PayoutBalance {
  balances: { currency: string; available_cents: number; pending_cents: number }[];
  settings: PayoutSettings;
  payouts_enabled: boolean;
  auto_withdrawn: PayoutResult[];
}

/** A learner's monthly subscription to a question bank product. */
export interface BankSubscription {
  id: string;
  product_id: string;
  product_slug: string;
  product_title: string;
  question_bank_id: string | null;
  status: 'incomplete' | 'trialing' | 'active' | 'past_due' | 'canceled' | 'unpaid' | 'incomplete_expired' | 'paused';
  has_access: boolean;
  price_cents: number;
  currency: string;
  current_period_end: string | null;
  cancel_at_period_end: boolean;
  cancel_reason: string | null;
  ended_at: string | null;
  created_at: string;
  payment_provider: string;
  external_subscription_id: string;
  is_test: boolean;
}

export const paymentService = {
  /** One-off purchase. `discountCode` is checked again on the server, which works out the price. */
  async startCheckout(productId: string, onNativeClosed?: (purchaseId: string) => void, discountCode?: string) {
    const res = await invokeFunction<{ url: string; purchase_id: string; test_mode: boolean }>('checkout', {
      product_id: productId,
      ...(discountCode ? { discount_code: discountCode } : {}),
    });
    await openExternal(res.url, onNativeClosed ? () => onNativeClosed(res.purchase_id) : undefined);
    return res;
  },

  /** Monthly subscription to a Store product. Access is granted only by the server once paid. */
  async startSubscription(productId: string, onNativeClosed?: () => void) {
    const res = await invokeFunction<{ url: string; test_mode: boolean }>('checkout', { product_id: productId, mode: 'subscription' });
    await openExternal(res.url, onNativeClosed);
    return res;
  },

  /** The learner's question bank subscriptions. */
  mySubscriptions: () => rpc<BankSubscription[]>('my_bank_subscriptions'),

  /** Payment provider's page to cancel a subscription or change the card (Plus and question banks). */
  async manageSubscriptions(returnTo = '/purchases') {
    const res = await invokeFunction<{ url: string }>('billing', { action: 'manage_subscription', return_to: returnTo });
    await openExternal(res.url);
  },

  /**
   * Ask the server to cancel subscriptions to products that just left the
   * Store (at period end, with an email). Fire-and-forget: a daily job does
   * the same, so a failure here only means a short wait.
   */
  processSubscriptionCancellations() {
    void invokeFunction('subscriptions', {}).catch(() => undefined);
  },

  async purchaseStatus(purchaseId: string) {
    const { data, error } = await supabase
      .from('purchases')
      .select('id, status, product_id, amount_cents, currency, is_test, failure_reason, products(slug, title, question_bank_id)')
      .eq('id', purchaseId)
      .maybeSingle();
    if (error) throw error;
    return data as unknown as
      | (Pick<Purchase, 'id' | 'status' | 'product_id' | 'amount_cents' | 'currency' | 'is_test' | 'failure_reason'> & {
          products: { slug: string; title: string; question_bank_id: string | null } | null;
        })
      | null;
  },

  // Development payment mode (refused server-side in production).
  devStatus: () => invokeFunction<{ enabled: boolean }>('dev-payments', { action: 'status' }),
  devCompleteCheckout: (purchaseId: string) => invokeFunction('dev-payments', { action: 'complete_checkout', purchase_id: purchaseId }),
  devFailCheckout: (purchaseId: string) => invokeFunction('dev-payments', { action: 'fail_checkout', purchase_id: purchaseId }),
  devCompleteOnboarding: () => invokeFunction('dev-payments', { action: 'complete_onboarding' }),
  devSimulateSale: (productId: string, userId: string) => invokeFunction('dev-payments', { action: 'simulate_sale', product_id: productId, user_id: userId }),
  devCompleteBankSubscription: (productId: string) => invokeFunction('dev-payments', { action: 'complete_bank_subscription', product_id: productId }),
  devBankSubscription: (
    action: 'renew_bank_subscription' | 'fail_bank_subscription_payment' | 'cancel_bank_subscription' | 'end_bank_subscription',
    subscriptionId: string,
  ) => invokeFunction('dev-payments', { action, subscription_id: subscriptionId }),

  // Provider payouts
  startPayoutOnboarding: () => invokeFunction<{ url: string; test_mode: boolean }>('provider-payments', { action: 'onboard' }),
  refreshPayoutAccount: () => invokeFunction<{ status: unknown }>('provider-payments', { action: 'refresh' }),
  payoutDashboard: () => invokeFunction<{ url: string }>('provider-payments', { action: 'dashboard' }),
  payoutBalance: () => invokeFunction<PayoutBalance>('provider-payments', { action: 'balance' }),
  withdraw: (amountCents: number, currency: string, requestId: string) =>
    invokeFunction<{ payout: PayoutResult }>('provider-payments', { action: 'withdraw', amount_cents: amountCents, currency, request_id: requestId }),
  setPayoutSettings: (autoWithdraw: boolean, thresholdCents: number, keepCents: number) =>
    rpc<PayoutSettings>('set_payout_settings', { _auto_withdraw: autoWithdraw, _threshold_cents: thresholdCents, _keep_cents: keepCents }),

  // Admin
  refund: (purchaseId: string, amountCents: number | undefined, reason: string) =>
    invokeFunction<{ refund_id: string; status: string }>('admin-payments', { action: 'refund', purchase_id: purchaseId, amount_cents: amountCents, reason }),
};

/** Whether the server is running the development payment provider. */
export function useDevPaymentMode() {
  return useQuery({
    queryKey: ['dev-payment-mode'],
    staleTime: Infinity,
    retry: false,
    queryFn: async () => {
      try {
        return (await paymentService.devStatus()).enabled;
      } catch {
        return false;
      }
    },
  });
}
